import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError, IOption } from 'app/shared/reducers/reducer.utils';
import { IOrganization, defaultValue } from 'app/shared/model/organization.model';
import { apiOrg } from 'app/config/axios-interceptor';

const initialState: EntityState<IOrganization> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  options: [],
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/org/org';

// Actions

export const getEntities = createAsyncThunk(
  'department/fetch_entity_list',
  async ({ searchText, page, size, sort, filters }: IQueryParams) => {
    return apiOrg.get<IOrganization>(`${apiUrl}/graph`, {
      params: {
        searchText,
        page,
        size,
        filters: filters && JSON.stringify(filters),
        sort: sort && JSON.stringify(sort),
        cacheBuster: new Date().getTime(),
      },
    });
  },
  { serializeError: serializeAxiosError },
);

export const getEntitiesByCompanyId = createAsyncThunk(
  'department/fetch_all_entity_list',
  async (companyId: string) => {
    return apiOrg.get<IOption[]>(`${apiUrl}/all?companyId=${companyId}`);
  },
  { serializeError: serializeAxiosError },
);

export const getEntity = createAsyncThunk(
  'department/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}?orgId=${id}`;
    return apiOrg.get<IOrganization>(requestUrl);
  },
  { serializeError: serializeAxiosError },
);

export const createEntity = createAsyncThunk(
  'department/create_entity',
  async (entity: IOrganization, thunkAPI) => {
    const result = await apiOrg.post<IOrganization>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const updateEntity = createAsyncThunk(
  'department/update_entity',
  async (entity: IOrganization, thunkAPI) => {
    const result = await apiOrg.put<IOrganization>(`${apiUrl}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

export const deleteEntity = createAsyncThunk(
  'department/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}?orgId=${id}`;
    const result = await apiOrg.delete<IOrganization>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError },
);

// slice

export const OrganizationSlice = createEntitySlice({
  name: 'department',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        const { data, headers } = action.payload;

        return {
          ...state,
          loading: false,
          entities: [data],
          totalItems: parseInt(headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEntitiesByCompanyId), (state, action) => {
        const { data } = action.payload;

        return {
          ...state,
          options: data,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
        state.entity = {};
      })
      .addMatcher(isPending(createEntity, updateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = OrganizationSlice.actions;

// Reducer
export default OrganizationSlice.reducer;
